import { Duration, Timestamp, timestampDate, timestampFromDate } from '@bufbuild/protobuf/wkt'
import { format, subYears } from 'date-fns'
import humanizeDuration from 'humanize-duration'
import ms from 'ms'
import pluralize from 'pluralize'

import { DATE_TEXT } from '@/lib/date-labels'

export const MILLISECONDS_IN_A_MINUTE = ms('1m')
export const MILLISECONDS_IN_A_DAY = ms('1d')
export const SECONDS_IN_A_DAY = MILLISECONDS_IN_A_DAY / 1000
export const SECONDS_IN_AN_HOUR = SECONDS_IN_A_DAY / 24
export const SECONDS_IN_A_MONTH = SECONDS_IN_A_DAY * 30
export const SECONDS_IN_A_YEAR = (MILLISECONDS_IN_A_DAY * 365) / 1000

export const formatCadence = (seconds?: bigint | number | null): string => {
  const secondsNumber = Number(seconds)
  if (
    seconds === null ||
    seconds === undefined ||
    !Number.isFinite(secondsNumber) ||
    secondsNumber <= 0 ||
    secondsNumber > SECONDS_IN_A_YEAR * 1000
  )
    return 'No cadence'

  if (secondsNumber < SECONDS_IN_A_DAY) return 'Daily'
  if (secondsNumber < SECONDS_IN_A_MONTH) {
    const days = Math.round(secondsNumber / SECONDS_IN_A_DAY)
    return days === 1 ? 'Daily' : `Every ${days} days`
  }
  if (secondsNumber < SECONDS_IN_A_YEAR) {
    const months = Math.round(secondsNumber / SECONDS_IN_A_MONTH)
    if (months === 12) {
      return 'Yearly'
    }
    return months === 1 ? 'Monthly' : `Every ${months} months`
  }

  if (secondsNumber < SECONDS_IN_A_YEAR * 1.5) {
    return 'Yearly'
  }

  const years = Math.round(secondsNumber / SECONDS_IN_A_YEAR)
  return `Every ${years} years`
}

const dateFormats = {
  date: "dd MMM ''yy",
  dateTime: 'dd MMM yyyy, HH:mm',
  shortDateTime: "d MMM ''yy",
  dateWithoutYear: 'dd MMM',
  longDate: 'EEEE d MMMM, yyyy, HH:mm',
  longDateWithSeconds: 'EEEE d MMMM, yyyy, HH:mm:ss',
  monthOnly: 'MMMM',
  monthWithYear: 'MMMyy',
  dateWithTextDay: 'EEE dd MMMM yyyy',
  monthDay: 'MMM d',
  monthDayYear: 'MMM d yyyy',
  monthDayYearComma: 'MMM d, yyyy',
  yyyyMMdd: 'yyyy-MM-dd',
  shortDateTimeWithDay: 'EEE d MMM, HH:mm',
}

export const formatTimestamp = (
  timestamp?: Timestamp,
  formatType: keyof typeof dateFormats = 'date',
) => {
  return timestamp && format(timestampDate(timestamp), dateFormats[formatType])
}

export const formatDate = (date: Date, formatType: keyof typeof dateFormats = 'date') => {
  return format(date, dateFormats[formatType])
}

export const formatMonth = (date: Date) => {
  const currentYear = new Date().getFullYear()
  const dateYear = date.getFullYear()
  const formatType = currentYear === dateYear ? 'monthOnly' : 'monthWithYear'
  return formatDate(date, formatType)
}

export const timeSince = (date: Date, shortUnit: boolean = false, compactUnit: boolean = false) => {
  const now = new Date()
  const diffInMillis = now.getTime() - date.getTime()
  return timeSinceMilliseconds(Math.abs(diffInMillis), shortUnit, compactUnit)
}

export const timeSinceMilliseconds = (
  ms: number,
  shortUnit: boolean = false,
  compactUnit: boolean = false,
  maxUnit: 'month' | 'day' = 'month',
) => {
  const seconds = Math.floor(ms / 1000)
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)
  const days = Math.floor(hours / 24)
  const months = Math.floor(days / 30)

  if (maxUnit === 'month' && months > 0) {
    const unit = compactUnit ? 'mo' : pluralize('month', months)
    return { value: months, unit }
  }

  if (days > 0) {
    return { value: days, unit: compactUnit ? 'd' : pluralize('day', days) }
  }

  if (hours > 0) {
    const unit = shortUnit ? 'hr' : compactUnit ? 'h' : pluralize('hour', hours)
    return { value: hours, unit }
  }

  if (minutes > 0) {
    const unit = shortUnit ? 'min' : compactUnit ? 'm' : pluralize('minute', minutes)
    return { value: minutes, unit }
  }

  const unit = shortUnit ? 'sec' : compactUnit ? 's' : pluralize('second', seconds)
  return { value: seconds, unit }
}

export const calculateTimeDiff = (time: Date) => {
  const now = new Date()
  return time.getTime() - now.getTime()
}

export const relativeTimeFormatted = (
  time?: Date,
  options: {
    // Switches to absolute date if time difference exceeds maxRelativeTimeMs
    maxRelativeTimeMs?: number
    shortUnit?: boolean
    formatType?: keyof typeof dateFormats
    compactUnit?: boolean
    fallback?: string
  } = {},
) => {
  if (time === undefined) return options.fallback ?? ''

  const timeDiff = calculateTimeDiff(time)
  const { unit, value } = timeSince(time, options.shortUnit ?? false, options.compactUnit ?? false)

  if (options.maxRelativeTimeMs && Math.abs(timeDiff) > options.maxRelativeTimeMs) {
    return formatDate(time, options.formatType ?? 'longDateWithSeconds')
  }

  // Sub-minute deltas collapse to "Just now" — second-level precision is
  // noise for anything a user reads (created/updated columns, activity feeds).
  if (Math.abs(timeDiff) < MILLISECONDS_IN_A_MINUTE) {
    return DATE_TEXT.JUST_NOW
  }

  const timeSpacing = options.compactUnit ? '' : ' '
  if (timeDiff > 0) {
    return `in ${value}${timeSpacing}${unit}`
  }
  return `${value}${timeSpacing}${unit} ago`
}

export const getTimestampDaysAgo = (daysAgo: number) => {
  const currentDate = new Date()
  currentDate.setDate(currentDate.getDate() - daysAgo)

  return timestampFromDate(currentDate)
}

// Anchors a calendar date (no clock time intended) to noon UTC so the same
// day appears on the wire across all timezones from UTC-12 to UTC+11. The
// 26h global timezone span means no single instant works everywhere; noon
// covers everyone except NZ/Fiji/Samoa/Kiribati.
export const toUTCNoon = (date: Date) =>
  new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 12))

export const protoDurationToString = (d?: Duration) => {
  if (!d) return ''
  return humanizeDuration(Number(d.seconds * BigInt(1000) + BigInt(d.nanos) / BigInt(1000000)), {
    largest: 1, // show only one unit of time
    round: true,
  })
}

// Stays in days past a month ("41d"), the way cycle and response times are read.
export const formatCompactDuration = (seconds: number) => {
  const { value, unit } = timeSinceMilliseconds(seconds * 1000, false, true, 'day')
  return `${value}${unit}`
}

export const ONE_YEAR_AGO = subYears(new Date(), 1)

export const getPreviousPeriodRange = (start: Date, end: Date) => {
  const periodDuration = end.getTime() - start.getTime()
  return {
    previousStart: new Date(start.getTime() - periodDuration),
    previousEnd: new Date(start.getTime()),
  }
}

export const calculateDaysBetween = (startDate?: Date, endDate?: Date): number => {
  if (!startDate || !endDate) return 0
  const diff = endDate.getTime() - startDate.getTime()
  return Math.max(0, Math.round(diff / MILLISECONDS_IN_A_DAY))
}

/**
 * Sort an array of objects by their timestamp field in descending order (newest first).
 * The accessor may return a protobuf Timestamp or a JS Date.
 *
 * @param items - The array to sort
 * @param getTimestamp - Function to extract the timestamp from each item
 * @returns A new sorted array with newest items first
 *
 * @example
 * ```ts
 * // Sort assessments by createdAt (protobuf Timestamp)
 * const sorted = sortByTimestamp(assessments, (a) => a.createdAt)
 *
 * // Sort Liveblocks threads by createdAt (JS Date)
 * const sorted = sortByTimestamp(threads, (t) => t.createdAt)
 * ```
 */
export function sortByTimestamp<T>(
  items: T[],
  getTimestamp: (item: T) => Timestamp | Date | undefined,
): T[] {
  return [...items].sort((a, b) => toMs(getTimestamp(b)) - toMs(getTimestamp(a)))
}

/**
 * Milliseconds since the epoch for a protobuf Timestamp or JS Date, or 0 when absent.
 * The 0 fallback makes a missing timestamp sort oldest and compare as "not newer".
 */
export function toMs(value: Timestamp | Date | undefined): number {
  if (!value) return 0
  return value instanceof Date ? value.getTime() : timestampDate(value).getTime()
}
